import type { Href } from "expo-router";
import { COLORS } from "../lib/brand";
import { router } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import {
  listMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../lib/notifications";
import type { NotificationKind, NotificationRow } from "../types/database";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_LIGHT = "rgba(28,43,26,0.45)";

function formatRelativeTime(iso: string): string {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return "";
  const diffMs = Date.now() - created;
  const minutes = Math.max(1, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function kindLabel(kind: NotificationKind): string {
  switch (kind) {
    case "team_invite_received":
      return "Team invite";
    case "team_member_joined":
      return "Team update";
    case "team_member_left":
      return "Team update";
    case "deliverable_due_soon":
      return "Due soon";
    case "deliverable_graded":
      return "Graded";
    case "gate_passed":
      return "Gate passed";
    case "gate_failed":
      return "Gate failed";
    case "placement_announced":
      return "Placement";
    case "auto_merge_warning":
      return "Auto-merge";
    case "dm_received":
      return "Message";
    case "connection_request":
      return "Connection";
    case "connection_accepted":
      return "Connection";
    case "prize_pool_awarded":
      return "Prize";
    case "judge_invited":
      return "Judging";
    case "announcement":
      return "Announcement";
    default:
      return "Activity";
  }
}

export type NotificationDropdownProps = {
  visible: boolean;
  /** Right-edge offset in px from the viewport — anchors dropdown under the bell. */
  anchorRight: number;
  /** Top offset in px — dropdown appears below the topbar. */
  anchorTop: number;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
  /**
   * Where the "Open inbox" footer routes. Each portal shell passes its own
   * portal-scoped inbox path (`/student/notifications`, `/client/notifications`)
   * so users always land in the right shell. Defaults to legacy `/notifications`
   * which itself redirects.
   */
  inboxHref?: string;
};

/**
 * Floating dropdown that opens from the bell icon. Shows the 12 most recent
 * notifications. Tap an item to mark read + jump to its target. Footer link
 * goes to the full Activity inbox (portal-scoped via `inboxHref`).
 */
export function NotificationDropdown({
  visible,
  anchorRight,
  anchorTop,
  onClose,
  onUnreadChange,
  inboxHref = "/notifications",
}: NotificationDropdownProps) {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    listMyNotifications(12).then((list) => {
      if (cancelled) return;
      setRows(list);
      setLoading(false);
      onUnreadChange?.(list.filter((n) => !n.read_at).length);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, onUnreadChange]);

  const unread = useMemo(() => rows.filter((n) => !n.read_at).length, [rows]);

  async function onPressItem(n: NotificationRow) {
    if (!n.read_at) {
      await markNotificationRead(n.id);
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, read_at: new Date().toISOString() } : r)));
      onUnreadChange?.(Math.max(0, unread - 1));
    }
    onClose();
    if (n.link_path) {
      router.push(n.link_path as Href);
    }
  }

  async function onMarkAllRead() {
    setBusy(true);
    await markAllNotificationsRead();
    const now = new Date().toISOString();
    setRows((prev) => prev.map((r) => ({ ...r, read_at: r.read_at ?? now })));
    onUnreadChange?.(0);
    setBusy(false);
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      {/* Backdrop — clicking outside closes the dropdown */}
      <Pressable
        onPress={onClose}
        style={[
          {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "transparent",
          },
          Platform.OS === "web" ? ({ cursor: "default" } as unknown as ViewStyle) : {},
        ]}
      />

      <View
        style={[
          {
            position: "absolute",
            top: anchorTop,
            right: anchorRight,
            width: 380,
            maxWidth: "92%" as unknown as number,
            backgroundColor: COLORS.WHITE.hex,
            borderWidth: 1,
            borderColor: SAND_DARK,
            borderRadius: 8,
            shadowColor: "#000",
            shadowOpacity: 0.18,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 12 },
            elevation: 12,
            overflow: "hidden",
          },
          Platform.OS === "web"
            ? ({ boxShadow: "0 24px 48px -16px rgba(0,0,0,0.30)" } as unknown as ViewStyle)
            : {},
        ]}
      >
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 py-3"
          style={{ borderBottomWidth: 1, borderBottomColor: "rgba(28,43,26,0.08)" }}
        >
          <View className="flex-row items-baseline gap-2">
            <Text
              className="text-[10px] font-bold uppercase"
              style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.8 }}
            >
              Activity
            </Text>
            {unread > 0 ? (
              <Text
                className="text-[10.5px]"
                style={{ fontFamily: "DMSans_500Medium", color: TEXT_MUTED, letterSpacing: 0.4 }}
              >
                · {unread} new
              </Text>
            ) : null}
          </View>
          {rows.some((r) => !r.read_at) ? (
            <Pressable
              onPress={() => void onMarkAllRead()}
              disabled={busy}
              style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
            >
              <Text
                className="text-[9.5px] font-bold uppercase"
                style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.4 }}
              >
                Mark all read
              </Text>
            </Pressable>
          ) : null}
        </View>

        {/* List */}
        <ScrollView style={{ maxHeight: 460 }}>
          {loading ? (
            <View className="items-center py-10">
              <ActivityIndicator size="small" color={FOREST} />
            </View>
          ) : rows.length === 0 ? (
            <View className="items-center py-10 px-6">
              <Text
                className="text-center text-[13px]"
                style={{ fontFamily: "DMSans_400Regular", color: TEXT_LIGHT }}
              >
                {`Nothing new — we'll let you know when something happens.`}
              </Text>
            </View>
          ) : (
            rows.map((n, idx) => (
              <NotificationRowItem
                key={n.id}
                row={n}
                onPress={() => void onPressItem(n)}
                first={idx === 0}
              />
            ))
          )}
        </ScrollView>

        {/* Footer */}
        <Pressable
          onPress={() => {
            onClose();
            router.push(inboxHref as Href);
          }}
          style={[
            {
              paddingHorizontal: 16,
              paddingVertical: 11,
              borderTopWidth: 1,
              borderTopColor: "rgba(28,43,26,0.08)",
              backgroundColor: "rgba(28,43,26,0.025)",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
          ]}
        >
          <Text
            className="text-[10.5px] font-bold uppercase"
            style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.6 }}
          >
            Open inbox
          </Text>
          <Text style={{ color: AMBER, fontSize: 13, fontFamily: "DMSans_500Medium" }}>→</Text>
        </Pressable>
      </View>
    </Modal>
  );
}

function NotificationRowItem({
  row,
  onPress,
  first,
}: {
  row: NotificationRow;
  onPress: () => void;
  first: boolean;
}) {
  const [hover, setHover] = useState(false);
  const isUnread = !row.read_at;
  const webHover =
    Platform.OS === "web"
      ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
      : {};

  return (
    <Pressable
      {...webHover}
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 16,
          paddingVertical: 12,
          borderTopWidth: first ? 0 : 1,
          borderTopColor: "rgba(28,43,26,0.06)",
          backgroundColor:
            Platform.OS === "web" && hover
              ? "rgba(28,43,26,0.035)"
              : isUnread
                ? "rgba(181,135,58,0.04)"
                : "transparent",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: 10,
        },
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {},
      ]}
    >
      {/* Unread indicator (left bar) */}
      <View
        style={{
          width: 3,
          alignSelf: "stretch",
          borderRadius: 2,
          backgroundColor: isUnread ? AMBER : "transparent",
        }}
      />
      <View className="min-w-0 flex-1">
        <View className="flex-row items-baseline justify-between gap-2">
          <Text
            className="text-[9px] font-bold uppercase"
            style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 1.4 }}
            numberOfLines={1}
          >
            {kindLabel(row.kind)}
          </Text>
          <Text
            className="text-[10px]"
            style={{ fontFamily: "DMSans_400Regular", color: TEXT_LIGHT }}
          >
            {formatRelativeTime(row.created_at)}
          </Text>
        </View>
        <Text
          className="mt-0.5 text-[14px] leading-5 text-forest"
          style={{
            fontFamily: isUnread ? "CormorantGaramond_600SemiBold" : "CormorantGaramond_500Medium",
          }}
          numberOfLines={2}
        >
          {row.title}
        </Text>
        {row.body ? (
          <Text
            className="mt-0.5 text-[12px] leading-[17px]"
            style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}
            numberOfLines={2}
          >
            {row.body}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}
